import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Leaf, Package, RotateCcw, Search, Truck } from "lucide-react";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";
import { useShop } from "@/context/ShopContext";
import { useContent } from "@/context/ContentContext";

const HERO_IMG = "https://images.unsplash.com/photo-1590422749897-47036da0b0ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxoYW5kbWFkZSUyMGNlcmFtaWMlMjBtdWclMjBwb3R0ZXJ5fGVufDB8fHx8MTc5MTgwNjYxNHww&ixlib=rb-4.1.0&q=85";
const POT_IMG = "https://images.unsplash.com/photo-1782351119423-3099437f3739?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHw0fHxjZXJhbWljJTIwdmFzZSUyMHBsYW50ZXIlMjBwb3R0ZXJ5JTIwc3R1ZGlvfGVufDB8fHx8MTc5MTgwNjYxNHww&ixlib=rb-4.1.0&q=85";
const KILN_IMG = "https://images.unsplash.com/photo-1760018890645-28c8312cd7cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxjZXJhbWljJTIwdmFzZSUyMHBsYW50ZXIlMjBwb3R0ZXJ5JTIwc3R1ZGlvfGVufDB8fHx8MTc5MTgwNjYxNHww&ixlib=rb-4.1.0&q=85";

const Line = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1.5 -mb-1.5">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.95, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const VALUE_ICONS = [Truck, Leaf, RotateCcw, Package];
const VALUE_DEFAULTS = [
  { title: "Free shipping", desc: "Insured delivery, free over ₹2,500" },
  { title: "Food-safe", desc: "Lead-free mineral glazes throughout" },
  { title: "Easy returns", desc: "7-day returns on damaged pieces" },
  { title: "Packed with care", desc: "Secure, crushproof packaging" },
];

const Home = () => {
  const { products, categories } = useShop();
  const { c } = useContent();
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const featured = products.filter((p) => p.featured).slice(0, 4);
  const shelf = products.slice(0, 8);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImage = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yWord = useTransform(scrollYProgress, [0, 1], [0, -70]);

  const marqueeItems = c("marquee_items", "").split("\n").map((s) => s.trim()).filter(Boolean);

  return (
    <div>
      {/* ---------- HERO ---------- */}
      <section ref={heroRef} className="relative overflow-hidden pt-32 pb-14 lg:pt-40 lg:pb-24">
        <motion.span
          style={{ y: yWord }}
          aria-hidden="true"
          className="display-outline pointer-events-none absolute -top-6 left-1/2 -translate-x-1/2 select-none font-serif text-[24vw] font-black leading-none tracking-tight lg:text-[19rem]"
        >
          RIAAS
        </motion.span>

        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="font-mono text-xs font-semibold uppercase tracking-[0.28em] text-terra"
            >
              {c("hero_eyebrow", "Handmade ceramic home decor")}
            </motion.p>

            <h1 className="mt-5 font-serif text-4xl font-medium leading-[1.08] tracking-tight text-espresso sm:text-5xl lg:text-6xl">
              <Line delay={0.25}>{c("hero_line1", "Beautiful ceramics,")}</Line>
              <Line delay={0.42}>
                {c("hero_line2a", "made to be")} <em className="italic text-terra">{c("hero_line2b", "lived with.")}</em>
              </Line>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 max-w-md text-base leading-relaxed text-clay/80"
            >
              {c("hero_subtext", "")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/shop"
                data-testid="hero-explore-button"
                className="group flex items-center gap-2 rounded-full bg-espresso px-7 py-3.5 text-sm font-semibold text-cream transition-colors duration-200 hover:bg-terra active:scale-[0.98]"
              >
                {c("hero_cta_primary", "Shop the collection")}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/shop?cat=Mugs"
                className="flex items-center gap-1.5 rounded-full border border-clay/25 px-7 py-3.5 text-sm font-semibold text-clay transition-colors duration-200 hover:border-terra hover:text-terra"
              >
                {c("hero_cta_secondary", "Bestselling mugs")} <ArrowUpRight className="h-4 w-4" />
              </Link>
            </motion.div>

            <motion.form
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
              onSubmit={(e) => {
                e.preventDefault();
                if (query.trim()) navigate(`/shop?q=${encodeURIComponent(query.trim())}`);
              }}
              className="mt-8 flex max-w-md items-center gap-2 rounded-full border border-linen bg-white p-1.5 shadow-sm"
              data-testid="home-search-form"
            >
              <Search className="ml-3 h-4 w-4 shrink-0 text-clay/40" />
              <input
                data-testid="home-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search mugs, planters, decor…"
                className="h-10 w-full bg-transparent text-sm text-espresso outline-none"
              />
              <button
                type="submit"
                data-testid="home-search-button"
                className="shrink-0 rounded-full bg-terra px-5 py-2.5 text-xs font-semibold uppercase tracking-widest text-white transition-colors hover:bg-terra-dark"
              >
                Search
              </button>
            </motion.form>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.15 }}
              className="mt-12 flex flex-wrap gap-x-8 gap-y-3 font-mono text-[10px] uppercase tracking-[0.22em] text-clay/60"
            >
              <span className="flex items-center gap-2"><Truck className="h-3.5 w-3.5 text-terra" /> {c("hero_chip1", "Free shipping over ₹2,500")}</span>
              <span className="flex items-center gap-2"><Leaf className="h-3.5 w-3.5 text-sage" /> {c("hero_chip2", "Food-safe glazes")}</span>
              <span className="flex items-center gap-2"><RotateCcw className="h-3.5 w-3.5 text-ochre" /> {c("hero_chip3", "Easy 7-day returns")}</span>
            </motion.div>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              style={{ y: yImage }}
              className="relative"
            >
              <div className="overflow-hidden rounded-b-[2rem] rounded-t-[11rem] border border-linen shadow-[0_30px_60px_-20px_rgba(46,36,31,0.35)]">
                <img src={c("hero_image", HERO_IMG)} alt="Handcrafted stoneware mugs in stacked composition" className="aspect-[4/5] w-full object-cover" />
              </div>
              <div className="float-soft absolute -left-4 top-16 rounded-full border border-linen bg-white/90 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-clay shadow-lg backdrop-blur sm:-left-8">
                {c("hero_badge1", "Bestsellers restocked")}
              </div>
              <div className="float-soft absolute -right-3 bottom-20 rounded-full bg-terra px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white shadow-lg [animation-delay:1.4s] sm:-right-6">
                {c("hero_badge2", "Sale · save up to ₹800")}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee items={marqueeItems} />

      {/* ---------- CATEGORY TILES ---------- */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-24">
        <Reveal>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.28em] text-terra">{c("categories_eyebrow", "Shop by category")}</p>
          <h2 className="mt-3 max-w-lg font-serif text-2xl tracking-tight text-espresso sm:text-3xl lg:text-4xl">
            {c("categories_title", "Find your piece faster")}
          </h2>
        </Reveal>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {(categories.length
            ? categories.map((cat, i) => ({ name: cat.name, label: cat.label, desc: cat.desc, img: cat.image || [HERO_IMG, POT_IMG, KILN_IMG][i % 3] }))
            : [
                { name: "Mugs", label: "Mugs & Drinkware", desc: "Mugs, cups, tumblers & glasses", img: c("cat_mugs_image", HERO_IMG) },
                { name: "Planters", label: "Planters & Pots", desc: "Breathable homes for your greens", img: c("cat_planters_image", POT_IMG) },
                { name: "Decor Items", label: "Tabletop & Decor", desc: "Cruet sets, dishes & sculptural pots", img: c("cat_decor_image", KILN_IMG) },
              ]
          ).map((t, i) => (
            <Reveal key={t.name} delay={Math.min(i * 0.1, 0.5)}>
              <Link
                to={`/shop?cat=${encodeURIComponent(t.name)}`}
                data-testid={`category-tile-${t.name.toLowerCase().replace(/[\s&]+/g, "-")}`}
                className="group relative block overflow-hidden rounded-2xl"
              >
                <img src={t.img} alt={t.label} className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]" />
                <div className="absolute inset-0 bg-gradient-to-t from-espresso/80 via-espresso/10 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-6">
                  <div>
                    <p className="font-serif text-xl text-cream sm:text-2xl">{t.label}</p>
                    <p className="mt-1 text-xs text-cream/70">{t.desc}</p>
                  </div>
                  <span className="flex h-10 w-10 items-center justify-center rounded-full border border-cream/40 text-cream transition-all duration-300 group-hover:bg-terra group-hover:border-terra">
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- BESTSELLERS ---------- */}
      <section className="border-y border-linen bg-sand/70">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-24">
          <Reveal>
            <div className="flex items-end justify-between gap-6">
              <div>
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.28em] text-terra">{c("bestsellers_eyebrow", "Customer favourites")}</p>
                <h2 className="mt-3 font-serif text-2xl tracking-tight text-espresso sm:text-3xl lg:text-4xl">{c("bestsellers_title", "Most loved right now")}</h2>
              </div>
              <Link to="/shop" className="group hidden items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] text-clay transition-colors hover:text-terra sm:flex">
                View all <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
          <div className="no-scrollbar mt-10 flex snap-x gap-6 overflow-x-auto pb-2 lg:grid lg:grid-cols-4 lg:overflow-visible">
            {featured.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08} className="w-64 shrink-0 snap-start lg:w-auto">
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- FULL SHELF ---------- */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-28">
        <Reveal>
          <div className="flex items-end justify-between gap-6">
            <div>
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.28em] text-terra">{c("shelf_eyebrow", "The collection")}</p>
              <h2 className="mt-3 font-serif text-2xl tracking-tight text-espresso sm:text-3xl lg:text-4xl">{c("shelf_title", "Browse every piece")}</h2>
            </div>
            <Link to="/shop" className="group hidden items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] text-clay transition-colors hover:text-terra sm:flex">
              Shop all <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
        <div className="mt-10 grid grid-cols-2 gap-x-5 gap-y-10 lg:grid-cols-4">
          {shelf.map((p, i) => (
            <Reveal key={p.id} delay={Math.min(i * 0.05, 0.3)}>
              <ProductCard product={p} />
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-12 text-center">
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 rounded-full border border-clay/25 px-8 py-3.5 text-sm font-semibold text-clay transition-colors duration-200 hover:border-terra hover:text-terra"
          >
            View the full collection <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>

      {/* ---------- VALUE STRIP ---------- */}
      <section className="border-y border-linen bg-sand/70">
        <div className="mx-auto grid max-w-7xl gap-8 px-5 py-14 sm:grid-cols-2 sm:px-8 lg:grid-cols-4">
          {VALUE_DEFAULTS.map((v, i) => {
            const Icon = VALUE_ICONS[i];
            return (
              <Reveal key={v.title} delay={i * 0.08} className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-terra-light text-terra-dark">
                  <Icon className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-semibold text-espresso">{c(`value${i + 1}_title`, v.title)}</p>
                  <p className="mt-1 text-sm text-clay/60">{c(`value${i + 1}_desc`, v.desc)}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ---------- CTA ---------- */}
      <section className="relative overflow-hidden bg-espresso">
        <span aria-hidden="true" className="display-outline pointer-events-none absolute -bottom-10 left-1/2 -translate-x-1/2 select-none font-serif text-[18vw] font-black leading-none lg:text-[13rem]">
          CLAY
        </span>
        <div className="relative mx-auto max-w-7xl px-5 py-24 text-center sm:px-8 lg:py-32">
          <Reveal>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.28em] text-ochre">{c("cta_eyebrow", "Handpicked for your home")}</p>
            <h2 className="mx-auto mt-4 max-w-2xl font-serif text-3xl leading-tight tracking-tight text-cream sm:text-4xl lg:text-5xl">
              {c("cta_title", "Your table deserves something made by a person.")}
            </h2>
            <Link
              to="/shop"
              data-testid="cta-shop-button"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-terra px-8 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-terra-dark active:scale-[0.98]"
            >
              {c("cta_button", "Shop handmade ceramics")} <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
